import { useMutation } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import { api, getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import { useAssistantsByIds } from './useAssistants'
import { usePhoneNumber } from './usePhoneNumbers'

/**
 * Request config for the try endpoint. The load balancer matches per-number
 * overrides on the `Called` query param, like it does for calls, so a number
 * pinned to a color is tested on that color. The API ignores it.
 */
export function tryRequestConfig(assistantPhoneNumber?: string | null): AxiosRequestConfig {
  return {
    params: {
      ...(assistantPhoneNumber && { Called: assistantPhoneNumber }),
    },
  }
}

/**
 * Resolve the assistant's phone number; `isLoading` is false once it is known
 * or known to be absent.
 */
export function useAssistantPhoneNumber(assistantId: number) {
  const assistants = useAssistantsByIds([assistantId], !!assistantId)
  const assistant = assistants.data?.[0]
  // The assistant payload may only carry phone_number_id
  const phoneNumber = usePhoneNumber(assistant?.phone_number ? undefined : assistant?.phone_number_id)
  return {
    phoneNumber: assistant?.phone_number ?? phoneNumber.data?.number,
    isLoading: assistants.isLoading || phoneNumber.isLoading,
  }
}

/**
 * Message content type for chat messages
 */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content?: string | null
  function_call?: {
    name: string
    arguments: string
  }
  tool_calls?: Array<{
    id: string
    type: string
    function: {
      name: string
      arguments: string
    }
  }>
  tool_call_id?: string
  refusal?: Record<string, string>
}

/**
 * Request payload for trying an assistant
 */
export interface TryAssistantRequest {
  assistant_id: number
  test_conversation_id?: string | null
  messages: ChatMessage[]
  caller_number?: string | null
}

/**
 * Response from the try assistant endpoint
 */
export interface TryAssistantResponse {
  test_conversation_id: string
  messages: ChatMessage[]
  caller_number: string
}

/**
 * Query keys for try assistant
 */
export const tryAssistantKeys = {
  all: ['try-assistant'] as const,
  session: (assistantId: number, conversationId?: string) =>
    [...tryAssistantKeys.all, assistantId, conversationId] as const,
}

/**
 * Hook for trying an assistant with chat messages
 */
export function useTryAssistant(assistantPhoneNumber?: string | null) {
  return useMutation({
    mutationFn: async (request: TryAssistantRequest): Promise<TryAssistantResponse> => {
      const response = await api.post<TryAssistantResponse>(
        '/assistants/try',
        request,
        tryRequestConfig(assistantPhoneNumber)
      )
      return response.data
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Failed to send message', {
        description: message,
      })
    },
  })
}

/**
 * Hook for starting a new conversation
 */
export function useStartConversation(assistantPhoneNumber?: string | null) {
  return useMutation({
    mutationFn: async ({
      assistantId,
      callerNumber,
    }: {
      assistantId: number
      callerNumber?: string
    }): Promise<TryAssistantResponse> => {
      const response = await api.post<TryAssistantResponse>(
        '/assistants/try',
        {
          assistant_id: assistantId,
          messages: [],
          caller_number: callerNumber,
        },
        tryRequestConfig(assistantPhoneNumber)
      )
      return response.data
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Failed to start conversation', {
        description: message,
      })
    },
  })
}

export default useTryAssistant
